import { Edge, MarkerType, Node } from '@xyflow/react';

interface NodeData {
  deviceId?: string;
  deviceName?: string;
  deviceType?: string;
  camera?: any;
  speaker?: any;
  relay?: any;
  relatedComponentGroups?: string[];
  originalDevice?: {
    id: string;
  };
}

interface LinkingMode {
  active: boolean;
  deviceId?: string;
}

export const createEdges = (
  nodes: Node[], 
  hoveredCameraId: string | null, 
  linkingMode: LinkingMode
): Edge[] => {
  const edges: Edge[] = [];

  // Add edges for speaker connections
  nodes.filter(node => node.type === 'speaker').forEach(speakerNode => {
    const speakerData = speakerNode.data as NodeData;
    const relatedComponentGroups = speakerData.relatedComponentGroups || [];
    
    const linkedCameras = nodes.filter(node => {
      if (node.type !== 'camera') return false;
      const cameraData = node.data as NodeData;
      const originalDeviceId = cameraData.camera?.originalDevice?.id;
      return originalDeviceId && relatedComponentGroups.includes(originalDeviceId);
    });
    
    linkedCameras.forEach((cameraNode) => {
      const cameraId = (cameraNode.data as any).deviceId;
      const speakerId = (speakerNode.data as any).deviceId;
      
      // Only show connection on hover of camera or speaker
      const showConnection = hoveredCameraId === cameraId || hoveredCameraId === speakerId;
      if (showConnection) {
      }
      
      // During linking mode, only show connections for the current linking device
      const isDimmed = linkingMode.active 
        ? (speakerId !== linkingMode.deviceId) 
        : !showConnection;
      
      // Check if camera is in bottom row by looking at relative position to speaker
      const cameraY = cameraNode.position.y;
      const speakerY = speakerNode.position.y;
      // Bottom row cameras are closer to speakers (within 150px), top row are further (200+ px)
      const distanceToSpeaker = speakerY - cameraY;
      const isBottomRowCamera = distanceToSpeaker < 180; // Bottom row is closer to speakers
      
      edges.push({
        id: `camera-${cameraId}-speaker-${speakerId}`,
        source: `camera-${cameraId}`,
        target: speakerNode.id,
        sourceHandle: isBottomRowCamera ? 'top' : 'bottom', // Bottom cameras use top handle, top cameras use bottom handle
        targetHandle: isBottomRowCamera ? 'bottom' : 'top', // Bottom cameras connect to bottom, top cameras to top
        type: 'smoothstep',
        style: { 
          stroke: isDimmed ? '#254442' : '#4ade80', 
          strokeWidth: 2,
          opacity: 1
        },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: isDimmed ? '#254442' : '#4ade80',
        },
        animated: !isDimmed,
        zIndex: isDimmed ? 445 : 455,
      });
    });
  });
  
  // Add edges for relay connections
  nodes.filter(node => node.type === 'relay').forEach(relayNode => {
    const relayData = relayNode.data as NodeData;
    const relatedComponentGroups = relayData.relatedComponentGroups || [];
    
    const linkedCameras = nodes.filter(node => {
      if (node.type !== 'camera') return false;
      const cameraData = node.data as NodeData;
      const originalDeviceId = cameraData.camera?.originalDevice?.id;
      return originalDeviceId && relatedComponentGroups.includes(originalDeviceId);
    });

    linkedCameras.forEach((cameraNode) => {
      const cameraId = (cameraNode.data as any).deviceId;
      const relayId = (relayNode.data as any).deviceId;
      
      // Only show connection on hover of camera or relay
      const showConnection = hoveredCameraId === cameraId || hoveredCameraId === relayId;
      if (showConnection) {
      }
      
      // During linking mode, only show connections for the current linking device
      const isDimmed = linkingMode.active 
        ? (relayId !== linkingMode.deviceId) 
        : !showConnection;
      
      // Check if camera is in bottom row by looking at relative position to relay
      const cameraY = cameraNode.position.y;
      const relayY = relayNode.position.y;
      // Bottom row cameras are closer to relays (within 300px), top row are further (350+ px)
      const distanceToRelay = relayY - cameraY;
      const isBottomRowCamera = distanceToRelay < 300; // Bottom row is closer to relays
      
      edges.push({
        id: `camera-${cameraId}-relay-${relayId}`,
        source: `camera-${cameraId}`,
        target: relayNode.id,
        sourceHandle: isBottomRowCamera ? 'top' : 'bottom', // Bottom cameras use top handle, top cameras use bottom handle
        targetHandle: isBottomRowCamera ? 'bottom' : 'top', // Bottom cameras connect to bottom, top cameras to top
        type: 'smoothstep',
        style: { 
          stroke: isDimmed ? '#2a3135' : '#facc15', 
          strokeWidth: 2,
          opacity: 1
        },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: isDimmed ? '#2a3135' : '#facc15',
        },
        animated: !isDimmed,
        zIndex: isDimmed ? 445 : 455,
      });
    });
  });

  return edges;
};